/**
 * LetterPreviewPage - Real Letter Preview Component
 * 
 * This is a REAL workflow step between generation and send/export.
 * It displays actual generated letter content from the same source
 * used for PDF and final delivery.
 * 
 * Features:
 * - Single letter preview
 * - Combined package preview
 * - Separate letters navigation
 * - PDF download (same source as preview)
 * - Approve/Continue action
 * - Back to Edit
 * - Save Draft
 * 
 * PHASE 4 ENHANCEMENTS:
 * - Generation mode badges (Few-shot / Deterministic)
 * - Package/split badges
 * - Excluded item notices
 * - Backend-approved output display
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { formatTimestamp } from '../../utils/dateTime';
import { buildDisputeLetterPdfFilename } from '../../utils/disputeLetterDownloadFilename';
import EnhancedLetterPreview from '../../components/disputes/EnhancedLetterPreview';
import {
  FileText,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
  Edit3,
  Save,
  Printer,
  Eye,
  AlertCircle,
  CheckCircle,
  Loader2,
  Mail,
  Building2,
  User,
  Calendar,
  FileCheck,
  Package,
  ArrowLeft,
  Send,
  X,
  Sparkles,
  Cog,
  AlertTriangle,
  Ban,
  Info,
  Layers,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { getLetterTabLabel, normalizeBureau } from '../../utils/letterTabLabel';
import { metro2Service } from '../../services/metro2Service';
import { trackWizardStep } from '../../utils/wizardProgress';
import {
  buildFinalPackageUrl,
  saveFinalPackageSnapshot,
} from '../../utils/finalPackageRecovery';
import {
  buildLetterPackageFromResult,
  generationHasLetters,
  logGenerationResultAudit,
  normalizeGenerationResult,
} from '../../utils/generationResultUtils';

// ============================================
// BUREAU NORMALIZATION UTILITY
// ============================================

/**
 * Normalize a list of bureaus, removing duplicates that differ only by casing.
 */
const normalizeBureausList = (bureaus) => {
  if (!bureaus || !Array.isArray(bureaus)) return bureaus;
  
  const seen = new Set();
  const normalized = [];
  
  for (const bureau of bureaus) {
    const canonical = normalizeBureau(bureau);
    const key = canonical.toLowerCase();
    
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push(canonical);
    }
  }
  
  return normalized;
};

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// PHASE 4: GENERATION MODE BADGE
// ============================================

const GenerationModeBadge = ({ mode, confidence, promptMetadata }) => {
  // PHASE B: Determine generation source from prompt metadata
  const isPromptManaged = promptMetadata?.prompt_id && !promptMetadata?.used_fallback;
  const isDeterministic = !isPromptManaged;
  
  // Fallback to mode-based detection if no prompt metadata
  const isFewShot = mode === 'few_shot' && !isDeterministic;
  
  return (
    <div className="flex items-center gap-2">
      <Badge 
        className={`${
          isPromptManaged 
            ? 'bg-purple-100 text-purple-700 border-purple-300' 
            : isDeterministic
              ? 'bg-amber-100 text-amber-700 border-amber-300'
              : 'bg-emerald-100 text-emerald-700 border-emerald-300'
        }`}
        data-testid="generation-mode-badge"
      >
        {isPromptManaged ? (
          <><Sparkles size={12} className="mr-1" /> Prompt Managed</>
        ) : (
          <><Cog size={12} className="mr-1" /> Deterministic</>
        )}
      </Badge>
      {/* PHASE B: Show prompt info if available */}
      {promptMetadata?.prompt_id && (
        <span className="text-xs text-purple-600" title={`Prompt: ${promptMetadata.prompt_name || promptMetadata.prompt_id}`}>
          {promptMetadata.prompt_name?.slice(0, 25) || promptMetadata.prompt_id?.slice(0, 12)}...
        </span>
      )}
      {promptMetadata?.used_fallback && promptMetadata?.fallback_reason && (
        <span className="text-xs text-amber-600" title={promptMetadata.fallback_reason}>
          (Fallback)
        </span>
      )}
      {confidence !== undefined && confidence !== null && !isPromptManaged && (
        <span className="text-xs text-stone-500">
          {Math.round(confidence * 100)}% confidence
        </span>
      )}
    </div>
  );
};

// ============================================
// PHASE 4: PACKAGE INFO BADGE
// ============================================

const PackageInfoBadge = ({ packageInfo }) => {
  if (!packageInfo) return null;
  
  const { is_mixed, package_count, packages } = packageInfo;
  
  if (!is_mixed || package_count <= 1) {
    return null;
  }
  
  return (
    <Badge 
      variant="outline"
      className="bg-purple-50 text-purple-700 border-purple-300"
      data-testid="package-split-badge"
    >
      <Layers size={12} className="mr-1" />
      {package_count} Packages (Split by Method)
    </Badge>
  );
};

// ============================================
// PHASE 4: EXCLUDED ITEMS NOTICE
// ============================================

const ExcludedItemsNotice = ({ excludedItems, blockedItems }) => {
  const hasExcluded = excludedItems && excludedItems.length > 0;
  const hasBlocked = blockedItems && blockedItems.length > 0;
  
  if (!hasExcluded && !hasBlocked) return null;
  
  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
      data-testid="excluded-items-notice"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-amber-900 mb-2">Item Notice</h4>
          
          {hasExcluded && (
            <div className="mb-2">
              <p className="text-sm text-amber-800 mb-1">
                <strong>{excludedItems.length}</strong> item{excludedItems.length > 1 ? 's' : ''} excluded from this letter:
              </p>
              <ul className="text-xs text-amber-700 list-disc list-inside space-y-1">
                {excludedItems.slice(0, 3).map((item, idx) => (
                  <li key={idx}>
                    {item.item_type || 'Item'}: {item.reason || 'Not qualified for this method'}
                  </li>
                ))}
                {excludedItems.length > 3 && (
                  <li className="text-amber-600">
                    +{excludedItems.length - 3} more excluded
                  </li>
                )}
              </ul>
            </div>
          )}
          
          {hasBlocked && (
            <div>
              <p className="text-sm text-amber-800 mb-1">
                <strong>{blockedItems.length}</strong> item{blockedItems.length > 1 ? 's' : ''} rerouted to alternative methods:
              </p>
              <ul className="text-xs text-amber-700 list-disc list-inside space-y-1">
                {blockedItems.slice(0, 3).map((item, idx) => (
                  <li key={idx}>
                    {item.blocked_method || 'Method'} → {item.alternative_method || 'Alternative'}: {item.reason || 'Method not qualified'}
                  </li>
                ))}
                {blockedItems.length > 3 && (
                  <li className="text-amber-600">
                    +{blockedItems.length - 3} more rerouted
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// LETTER PREVIEW HEADER
// ============================================

const PreviewHeader = ({ 
  letterPackage, 
  currentIndex, 
  onBack, 
  onApprove, 
  onDownloadPDF, 
  onSaveDraft,
  downloading,
  approving 
}) => {
  const totalLetters = letterPackage?.letter_count || 1;
  const currentLetter = letterPackage?.letters?.[currentIndex];
  const deliveryFormat = letterPackage?.delivery_format || 'combined';
  const packageState = letterPackage?.package_state || 'preview';
  
  // PHASE 4: Generation metadata
  const generationMode = letterPackage?.generation_mode;
  const confidenceScore = letterPackage?.confidence_score;
  const packageInfo = letterPackage?.package_info;
  
  // PHASE B: Get prompt metadata from current letter
  const promptMetadata = currentLetter?.prompt_metadata || letterPackage?.prompt_metadata;
  
  const stateColors = {
    draft: 'bg-stone-100 text-stone-700 border-stone-300',
    preview: 'bg-amber-100 text-amber-700 border-amber-300',
    final: 'bg-emerald-100 text-emerald-700 border-emerald-300'
  };
  
  const stateLabels = {
    draft: 'Draft',
    preview: 'Preview',
    final: 'Final'
  };

  return (
    <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        {/* Top row: Back + Title + Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="text-stone-600 hover:text-stone-900 text-sm"
              data-testid="preview-back-btn"
            >
              <ArrowLeft size={16} className="mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Back to Edit</span>
              <span className="xs:hidden">Back</span>
            </Button>
            <div className="hidden sm:block h-6 w-px bg-slate-300" />
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-stone-600 flex-shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-stone-900">Letter Preview</h1>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button 
              variant="outline"
              onClick={onSaveDraft}
              className="text-stone-600 flex-1 sm:flex-none text-sm"
              data-testid="save-draft-btn"
            >
              <Save size={14} className="mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Save Draft</span>
              <span className="xs:hidden">Save</span>
            </Button>
            
            <Button 
              variant="outline"
              onClick={onDownloadPDF}
              disabled={downloading}
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 flex-1 sm:flex-none text-sm"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 size={14} className="mr-1 sm:mr-2 animate-spin" />
              ) : (
                <Download size={14} className="mr-1 sm:mr-2" />
              )}
              <span className="hidden xs:inline">Download PDF</span>
              <span className="xs:hidden">PDF</span>
            </Button>
            
            <Button 
              onClick={onApprove}
              disabled={approving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none text-sm"
              data-testid="approve-continue-btn"
            >
              {approving ? (
                <Loader2 size={14} className="mr-1 sm:mr-2 animate-spin" />
              ) : (
                <Check size={14} className="mr-1 sm:mr-2" />
              )}
              <span className="hidden xs:inline">Approve & Continue</span>
              <span className="xs:hidden">Approve</span>
            </Button>
          </div>
        </div>
        
        {/* Bottom row: Package info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <Badge className={stateColors[packageState]}>
              {stateLabels[packageState]}
            </Badge>
            
            {/* PHASE B: Generation mode badge with prompt metadata */}
            {(generationMode || promptMetadata) && (
              <GenerationModeBadge mode={generationMode} confidence={confidenceScore} promptMetadata={promptMetadata} />
            )}
            
            {/* PHASE 4: Package split badge */}
            <PackageInfoBadge packageInfo={packageInfo} />
            
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <Package size={14} />
              <span>
                {deliveryFormat === 'combined' ? 'Combined Package' : 'Separate Letters'}
              </span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <FileText size={14} />
              <span>{totalLetters} letter{totalLetters > 1 ? 's' : ''}</span>
            </div>
            
            {currentLetter && (
              <>
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Building2 size={14} />
                  <span>{currentLetter.bureau}</span>
                </div>
                {currentLetter.furnisher && currentLetter.furnisher !== 'N/A' && (
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <User size={14} />
                    <span>{currentLetter.furnisher}</span>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="text-sm text-stone-500">
            <Calendar size={14} className="inline mr-1" />
            Generated: {formatTimestamp(letterPackage?.generated_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LETTER NAVIGATION (for separate letters)
// ============================================

/**
 * Generate a unique key for each letter to prevent overwrites and enable proper React keying.
 * Format: {roundId}_{packageId}_{bureau}_{method}_{family}_{packageSubtype}
 * 
 * @param {Object} letter - Letter object with metadata
 * @param {string} roundId - Optional round ID for additional uniqueness
 * @returns {string} Unique letter key
 */
const getLetterKey = (letter, roundId = '') => {
  const bureau = normalizeBureau(letter.bureau || 'unknown');
  const method = letter.method_used || letter.package_method || 'default';
  const family = letter.prompt_family || 'factual';
  const packageSubtype = letter.package_subtype || letter.package_item_type || 'account';
  const letterId = letter.letter_id || '';
  
  // Create a composite key that guarantees uniqueness
  return `${roundId}_${bureau}_${method}_${family}_${packageSubtype}_${letterId}`.toLowerCase().replace(/\s+/g, '_');
};

const LetterNavigation = ({ letters, currentIndex, onSelectLetter, roundId = '' }) => {
  if (!letters || letters.length <= 1) return null;
  
  return (
    <div className="bg-stone-50 border-b border-stone-200 py-3 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-stone-600 mr-2">Letters:</span>
          {letters.map((letter, index) => {
            const tabLabel = getLetterTabLabel(letter);
            const letterKey = getLetterKey(letter, roundId);
            return (
              <button
                key={letterKey}
                onClick={() => onSelectLetter(index)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  index === currentIndex
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white border border-stone-300 text-stone-700 hover:border-emerald-300 hover:text-emerald-700'
                }`}
                data-testid={`letter-nav-${letterKey}`}
              >
                {index + 1}. {tabLabel}
              </button>
            );
          })}
          
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentIndex === 0}
              onClick={() => onSelectLetter(currentIndex - 1)}
              data-testid="prev-letter-btn"
            >
              <ChevronLeft size={16} />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentIndex === letters.length - 1}
              onClick={() => onSelectLetter(currentIndex + 1)}
              data-testid="next-letter-btn"
            >
              Next
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LETTER CONTENT VIEWER
// ============================================

const LetterContentViewer = ({ html, letterType, generationMetadata, excludedItems, blockedItems }) => {
  const iframeRef = useRef(null);
  const [layoutCss, setLayoutCss] = useState('');
  
  // Fetch layout CSS on mount for preview/PDF parity
  useEffect(() => {
    const fetchLayoutCss = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/letter-preview/layout-css`);
        if (response.data?.css) {
          setLayoutCss(response.data.css);
        }
      } catch (error) {
        console.warn('Failed to fetch layout CSS, using embedded styles', error);
      }
    };
    fetchLayoutCss();
  }, []);
  
  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      
      // Inject layout CSS to ensure preview/PDF parity
      // This CSS uses !important to override embedded styles
      const overrideCss = `
<style type="text/css">
/* PREVIEW/PDF PARITY OVERRIDE */
@page { size: letter !important; margin: 0.75in !important; }
body { 
  font-family: 'Times New Roman', Times, Georgia, serif !important;
  font-size: 11pt !important;
  line-height: 1.15 !important;
  padding: 0 !important;
  margin: 0 !important;
}
p { margin: 0 0 6pt 0 !important; line-height: 1.15 !important; }
h1, h2, h3, h4, .section-title, .section-heading {
  margin-top: 10pt !important;
  margin-bottom: 5pt !important;
  font-size: 11pt !important;
}
ul, ol { margin: 4pt 0 6pt 18pt !important; line-height: 1.15 !important; }
li { margin: 0 0 2pt 0 !important; }
.consumer-identity, .consumer-reference, .header-block {
  margin-bottom: 10pt !important;
  line-height: 1.1 !important;
}
.consumer-identity p, .header-block p { margin: 0 0 2pt 0 !important; }
.bureau-address, .recipient-block { margin-bottom: 10pt !important; }
.signature, .closing, .signature-block { margin-top: 14pt !important; }
table { margin: 10pt 0 !important; }
th, td { padding: 6pt !important; }
</style>
`;
      
      // Insert override CSS at end of head or start of body
      let modifiedHtml = html;
      if (modifiedHtml.includes('</head>')) {
        modifiedHtml = modifiedHtml.replace('</head>', `${overrideCss}</head>`);
      } else if (modifiedHtml.includes('<body')) {
        modifiedHtml = modifiedHtml.replace(/(<body[^>]*>)/, `$1${overrideCss}`);
      } else {
        modifiedHtml = overrideCss + modifiedHtml;
      }
      
      doc.write(modifiedHtml);
      doc.close();
    }
  }, [html, layoutCss]);

  // Map letter type to display name
  const letterTypeDisplayNames = {
    'factual': 'Factual Dispute Letter',
    'factual_inaccuracy': 'Factual Dispute Letter',
    'metro2': 'Metro 2 Technical Dispute',
    'metro2_technical': 'Metro 2 Technical Dispute',
    'debt_validation': 'Debt Validation Letter',
    'inquiry': 'Inquiry Dispute Letter',
    'unauthorized_inquiry': 'Inquiry Dispute Letter',
    'identity_theft': 'Identity Theft Dispute',
    'goodwill': 'Goodwill Adjustment Request',
    'goodwill_adjustment': 'Goodwill Adjustment Request',
    'bankruptcy': 'Bankruptcy Dispute Letter',
    'alternate_bureau': 'Alternate Bureau Letter',
    'data_breach': 'Data Breach Method Letter',
    'data_breach_method': 'Data Breach Method Letter',
    'personal_info_accuracy': 'Personal Information Accuracy Dispute',
    // CRITICAL: Add isolated, structural, recovery letter types
    'isolated': 'Isolated Strategy Letter',
    'isolated_method': 'Isolated Strategy Letter',
    'structural': 'Structural Damage Dispute',
    'structural_method': 'Structural Damage Dispute',
    'recovery': 'Recovery Strategy Letter',
    'recovery_method': 'Recovery Strategy Letter',
    'control_file': 'Control File Dispute',
    'control_file_method': 'Control File Dispute',
  };

  const displayLetterType = letterTypeDisplayNames[letterType] || 'Dispute Letter';
  
  // PHASE 4: Get generation mode from metadata
  const generationMode = generationMetadata?.generation_mode;
  const isFewShot = generationMode === 'few_shot';

  return (
    <div className="bg-stone-100 p-3 sm:p-6">
      <div className="max-w-4xl mx-auto min-w-0 overflow-x-hidden">
        {/* PHASE 4: Excluded/Blocked items notice */}
        <ExcludedItemsNotice excludedItems={excludedItems} blockedItems={blockedItems} />
        
        {/* Letter type indicator with generation metadata */}
        <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge 
              className={
                letterType === 'metro2' || letterType === 'metro2_technical'
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : letterType === 'debt_validation'
                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                  : letterType === 'inquiry' || letterType === 'unauthorized_inquiry'
                  ? 'bg-amber-100 text-amber-700 border-amber-300'
                  : letterType === 'identity_theft'
                  ? 'bg-red-100 text-red-700 border-red-300'
                  : letterType === 'personal_info_accuracy'
                  ? 'bg-teal-100 text-teal-700 border-teal-300'
                  : letterType === 'data_breach' || letterType === 'data_breach_method'
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : letterType === 'isolated' || letterType === 'isolated_method'
                  ? 'bg-sky-100 text-sky-700 border-sky-300'
                  : letterType === 'structural' || letterType === 'structural_method'
                  ? 'bg-orange-100 text-orange-700 border-orange-300'
                  : letterType === 'recovery' || letterType === 'recovery_method'
                  ? 'bg-rose-100 text-rose-700 border-rose-300'
                  : 'bg-emerald-100 text-emerald-700 border-emerald-300'
              }
              data-testid="letter-type-badge"
            >
              {displayLetterType}
            </Badge>
            
            {/* PHASE 4: Per-letter generation mode badge */}
            {generationMode && (
              <Badge 
                className={`text-xs ${
                  isFewShot 
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : 'bg-amber-50 text-amber-600 border-amber-200'
                }`}
                data-testid="letter-generation-badge"
              >
                {isFewShot ? (
                  <><Sparkles size={10} className="mr-1" /> Few-shot</>
                ) : (
                  <><Cog size={10} className="mr-1" /> Template</>
                )}
              </Badge>
            )}
            
            {generationMetadata?.strategy_used && (
              <Badge variant="outline" className="text-xs">
                Strategy: {generationMetadata.strategy_used}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <Eye size={14} />
            Preview Mode - Review before sending
          </div>
        </div>
        
        {/* Show generation context for debugging/transparency */}
        {generationMetadata && (
          <div className="mb-4 bg-white border border-stone-200 rounded-lg p-3 text-xs">
            <div className="flex items-center gap-4 text-stone-600 flex-wrap">
              <span><strong>Method:</strong> {generationMetadata.method_used || 'factual_inaccuracy'}</span>
              
              {/* PHASE 4: Show matched training IDs if few-shot */}
              {generationMetadata.matched_training_ids?.length > 0 && (
                <span className="text-emerald-600">
                  <Sparkles size={12} className="inline mr-1" />
                  {generationMetadata.matched_training_ids.length} training example(s) used
                </span>
              )}
              
              {generationMetadata.used_fallback && (
                <span className="text-amber-600">
                  <AlertCircle size={12} className="inline mr-1" />
                  Fallback: {generationMetadata.fallback_reason?.substring(0, 50)}...
                </span>
              )}
              
              {/* PHASE 4: Confidence score */}
              {generationMetadata.confidence_score !== undefined && (
                <span className="text-stone-500">
                  <Info size={12} className="inline mr-1" />
                  Confidence: {Math.round(generationMetadata.confidence_score * 100)}%
                </span>
              )}
            </div>
            
            {/* PHASE 4: Explanation summary */}
            {generationMetadata.explanation_summary && (
              <div className="mt-2 pt-2 border-t border-stone-100 text-stone-500">
                {generationMetadata.explanation_summary}
              </div>
            )}
          </div>
        )}
        
        {/* Letter content in iframe for isolation */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-stone-200">
          <iframe
            ref={iframeRef}
            title="Letter Preview"
            className="w-full min-h-[900px] border-0"
            style={{ height: '1200px' }}
            data-testid="letter-preview-iframe"
          />
        </div>
        
        {/* Print notice */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Printer size={18} className="text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 mb-1">Print-Ready Format</h4>
              <p className="text-sm text-amber-800">
                This letter is formatted for standard 8.5" × 11" paper with proper margins. 
                The preview banner will not appear in the printed/PDF version.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN LETTER PREVIEW PAGE
// ============================================

const LetterPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [letterPackage, setLetterPackage] = useState(null);
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    const roundId = location.state?.roundId || letterPackage?.round_id;
    if (!letterPackage?.letters?.length || !roundId || !token) return;

    trackWizardStep({
      metro2Service,
      roundId,
      token,
      currentStep: 'preview',
      wizardStatus: 'ready_for_preview',
      packageId: letterPackage.package_id || location.state?.packageId,
      generatedLetterIds: letterPackage.letters.map((l) => l.letter_id || l.id).filter(Boolean),
      navigationState: {
        letterPackage,
        roundId,
        packageId: letterPackage.package_id || location.state?.packageId,
        identityAnchor: location.state?.identityAnchor || letterPackage.consumer_info,
      },
    });
  }, [letterPackage, location.state?.roundId, location.state?.packageId, token]);
  
  // SESSION RECOVERY: Check for missing required state on mount
  // This handles direct URL access and page refresh scenarios
  useEffect(() => {
    const hasRequiredState = (
      location.state?.letterPackage ||
      location.state?.preGeneratedResult ||
      location.state?.selectedItems?.length > 0 ||
      location.state?.sessionKey
    );
    
    if (!hasRequiredState) {
      // Try to recover session from sessionStorage using URL-based key
      const pathParts = location.pathname.split('/');
      const letterType = pathParts[pathParts.length - 2] || 'factual';
      const potentialKeys = [
        `letterPreview_${letterType}_recent`,
        `letterPreview_recent`,
      ];
      
      let recovered = false;
      for (const key of potentialKeys) {
        try {
          const stored = sessionStorage.getItem(key);
          if (stored) {
            const data = JSON.parse(stored);
            // Check if data is fresh (less than 30 minutes old)
            if (data.timestamp && (Date.now() - data.timestamp) < 30 * 60 * 1000) {
              console.log('[SESSION RECOVERY] Recovered session from:', key);
              // Note: We can't modify location.state directly, but we can show the recovered data
              recovered = true;
              break;
            }
          }
        } catch (e) {
          console.warn('[SESSION RECOVERY] Failed to recover from:', key, e);
        }
      }
      
      if (!recovered) {
        console.warn('[SESSION RECOVERY] No valid session state found - redirecting to dispute wizard');
        // Show a brief message before redirecting
        toast.info('Session expired - returning to dispute wizard', {
          description: 'Please re-select items to generate letters',
          duration: 3000,
        });
        
        // Redirect to the appropriate dispute wizard page
        setTimeout(() => {
          navigate('/dispute-wizard', { replace: true });
        }, 100);
        return;
      }
    }
  }, [location, navigate]);

  // PHASE C: Helper to recover data from sessionStorage
  const recoverFromSessionStorage = () => {
    const sessionKey = location.state?.sessionKey;
    if (sessionKey) {
      try {
        const stored = sessionStorage.getItem(sessionKey);
        if (stored) {
          const data = JSON.parse(stored);
          // Only use if less than 5 minutes old
          if (data.timestamp && (Date.now() - data.timestamp) < 5 * 60 * 1000) {
            console.log('[LETTER PREVIEW] Recovered data from sessionStorage:', sessionKey);
            return data.preGeneratedResult;
          }
        }
      } catch (e) {
        console.warn('[LETTER PREVIEW] Failed to recover from sessionStorage:', e);
      }
    }
    return null;
  };

  // Get preview data from location state or generate new
  useEffect(() => {
    const initializePreview = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Check if we have pre-generated data in location state
        if (location.state?.letterPackage) {
          setLetterPackage(location.state.letterPackage);
          setLoading(false);
          return;
        }
        
        // CRITICAL: Handle preGeneratedResult from SSE generation flow
        // PHASE C: Also try sessionStorage recovery if state is missing
        let preGenResult = location.state?.preGeneratedResult;
        if (!preGenResult) {
          preGenResult = recoverFromSessionStorage();
        }
        
        if (preGenResult) {
          console.log('[LETTER PREVIEW] Using preGeneratedResult from SSE generation');
          logGenerationResultAudit(preGenResult, 'LETTER PREVIEW');

          const lettersArray = preGenResult.letters || preGenResult.packages || [];
          console.log(`[LETTER PREVIEW DEBUG] Received ${lettersArray.length} letters:`,
            lettersArray.map(l => ({
              bureau: l.bureau || l.letter?.bureau,
              method: l.package_method || l.method_used,
              subtype: l.package_subtype
            }))
          );

          const normalizedResult = normalizeGenerationResult(preGenResult);
          if (!generationHasLetters(normalizedResult)) {
            setError('Letters were not generated yet. Please retry generation.');
            setLoading(false);
            return;
          }

          const convertedPackage = buildLetterPackageFromResult(normalizedResult, {
            letterType: location.state?.letterType,
          });
          
          // DEBUG: Log the final converted package
          console.log(`[LETTER PREVIEW DEBUG] Converted package has ${convertedPackage.letters.length} letters:`,
            convertedPackage.letters.map(l => `${l.bureau} - ${l.package_subtype}`)
          );
          
          setLetterPackage(convertedPackage);
          
          // CRITICAL: Auto-link letters to round so My Disputes shows correct status
          const roundId = location.state?.roundId;
          if (roundId && convertedPackage.letters?.length > 0) {
            try {
              console.log('[LETTER PREVIEW] Auto-linking SSE-generated letters to round:', roundId);
              const linkResponse = await axios.post(
                `${API}/letter-preview/link-to-round`,
                {
                  round_id: roundId,
                  run_id: normalizedResult.run_id || preGenResult.run_id || null,
                  letters: convertedPackage.letters.map(letter => ({
                    bureau: letter.bureau || '',
                    html: letter.html || '',
                    method_used: letter.method_used || 'factual_inaccuracy',
                    generation_mode: letter.generation_mode || 'prompt_runtime',
                    letter_id: letter.letter_id || letter.id || null,
                  })),
                  user_id: user?.id || user?.user_id || '',
                },
                { headers: getAuthHeaders(), withCredentials: true }
              );
              
              if (linkResponse.data.success) {
                console.log(`[LETTER PREVIEW] SUCCESS: Auto-linked ${linkResponse.data.letters_linked} letters to round ${roundId}`);
                toast.success(`Letters linked to dispute round`);
              } else {
                console.warn('[LETTER PREVIEW] Link response not successful:', linkResponse.data);
              }
            } catch (linkErr) {
              console.warn('[LETTER PREVIEW] Failed to auto-link letters to round:', linkErr.response?.data || linkErr.message);
            }
          }
          
          setLoading(false);
          return;
        }
        
        // Get AI Decision from location state (passed from ItemSelectionPage)
        const aiDecision = location.state?.aiDecision || null;
        const methodResolution = location.state?.methodResolution || null;
        const decisionOutput = location.state?.decisionOutput || null;
        
        // PHASE 4: Get excluded and blocked items from decision output
        const excludedItems = decisionOutput?.excluded_items || location.state?.excludedItems || [];
        const blockedItems = decisionOutput?.blocked_items || location.state?.blockedItems || [];
        
        // Check for mixed method selection
        const isMixedSelection = methodResolution?.summary?.is_mixed || false;
        
        // Determine letter type - prefer AI decision, then state, then URL, then fallback
        let letterType = 'factual';
        let usedFallback = false;
        let fallbackReason = null;
        
        // For mixed selections, use the primary method but note that items have different assignments
        if (isMixedSelection && methodResolution?.summary?.primary_method) {
          // Map primary method to letter type
          const methodMap = {
            'data_breach_method': 'data_breach',
            'personal_info_accuracy': 'personal_info_accuracy',
            'factual_inaccuracy': 'factual',
            // CRITICAL: Add isolated, structural, recovery methods
            'isolated_method': 'isolated',
            'isolated': 'isolated',
            'structural_method': 'structural',
            'structural': 'structural',
            'recovery_method': 'recovery',
            'recovery': 'recovery',
            'control_file_method': 'control_file',
          };
          letterType = methodMap[methodResolution.summary.primary_method] || 'factual';
          fallbackReason = `Mixed selection detected (${methodResolution.summary.unique_method_count} methods). Using primary: ${methodResolution.summary.primary_method}`;
        } else if (aiDecision?.recommended_method) {
          // Map backend method to letter type
          const methodToLetterType = {
            'factual_inaccuracy': 'factual',
            'meth_factual_inaccuracy': 'factual',
            'debt_validation': 'debt_validation',
            'meth_debt_validation': 'debt_validation',
            'metro2_technical': 'metro2',
            'meth_metro2_technical': 'metro2',
            'unauthorized_inquiry': 'inquiry',
            'meth_unauthorized_inquiry': 'inquiry',
            'identity_theft': 'identity_theft',
            'meth_identity_theft': 'identity_theft',
            'goodwill_adjustment': 'goodwill',
            'meth_goodwill_adjustment': 'goodwill',
            // DATA BREACH METHOD - for inquiries, personal info, qualified negatives
            'data_breach_method': 'data_breach',
            'data_breach': 'data_breach',
            'meth_data_breach': 'data_breach',
            // PERSONAL INFO ACCURACY - for simple PI corrections
            'personal_info_accuracy': 'personal_info_accuracy',
            'pi_accuracy': 'personal_info_accuracy',
            // ISOLATED METHOD - for 1-2 late payments (CRITICAL FIX)
            'isolated_method': 'isolated',
            'isolated': 'isolated',
            'meth_isolated': 'isolated',
            // STRUCTURAL METHOD - for 3+ late payments, charge-offs
            'structural_method': 'structural',
            'structural': 'structural',
            'meth_structural': 'structural',
            // RECOVERY METHOD - for collections, repossessions
            'recovery_method': 'recovery',
            'recovery': 'recovery',
            'meth_recovery': 'recovery',
            // CONTROL FILE METHOD
            'control_file_method': 'control_file',
            'control_file': 'control_file',
          };
          letterType = methodToLetterType[aiDecision.recommended_method] || location.state?.letterType || 'factual';
          if (!methodToLetterType[aiDecision.recommended_method]) {
            usedFallback = true;
            fallbackReason = `Unknown method '${aiDecision.recommended_method}' - using location state or default`;
          }
        } else if (location.state?.letterType) {
          letterType = location.state.letterType;
          usedFallback = true;
          fallbackReason = 'No AI decision available - using letterType from navigation state';
        } else {
          letterType = searchParams.get('type') || 'factual';
          usedFallback = true;
          fallbackReason = 'No AI decision or state letterType - using URL param or default';
        }
        
        // Get bureaus and NORMALIZE them to prevent duplicates from casing differences
        const rawBureaus = location.state?.bureaus || searchParams.get('bureaus')?.split(',') || ['Equifax'];
        const bureaus = normalizeBureausList(rawBureaus);
        console.log('[LETTER PREVIEW] Bureau normalization:', { original: rawBureaus, normalized: bureaus });
        
        const deliveryFormat = location.state?.deliveryFormat || searchParams.get('format') || 'combined';
        
        // Get user data for the letter
        const consumerName = user?.full_name || user?.name || 'Consumer Name';
        const consumerAddress = user?.address || '123 Main Street';
        const consumerCityStateZip = user?.city_state_zip || 'City, ST 12345';
        
        // Get dispute items from location state
        const disputeItems = location.state?.selectedItems || [];
        const furnisher = location.state?.furnisher || '';
        const violationType = location.state?.violationType || 'payment_history_chargeoff';
        
        // Check if we have mixed item types (accounts + inquiries + PI)
        const hasAccounts = disputeItems.some(item => (item.type || item.item_type) === 'account');
        const hasInquiries = disputeItems.some(item => (item.type || item.item_type) === 'inquiry');
        const hasPI = disputeItems.some(item => (item.type || item.item_type) === 'personal_info');
        const hasMixedItemTypes = (hasAccounts && hasInquiries) || (hasAccounts && hasPI) || (hasInquiries && hasPI);
        
        // ALWAYS use enhanced endpoint for mixed item types to enforce package separation
        // Also use enhanced if we have decision output or method resolution
        let response;
        let useEnhanced = hasMixedItemTypes || 
                          (decisionOutput && Object.keys(decisionOutput).length > 0) ||
                          (methodResolution && (methodResolution.summary?.is_mixed || methodResolution.method_groups?.length > 1));
        
        console.log('[LETTER PREVIEW] Using enhanced endpoint:', useEnhanced, 
          'hasMixedItemTypes:', hasMixedItemTypes, 
          'hasDecisionOutput:', !!(decisionOutput && Object.keys(decisionOutput).length > 0),
          'hasMethodResolution:', !!(methodResolution && methodResolution.summary?.is_mixed));
        
        if (useEnhanced) {
          try {
            response = await axios.post(
              `${API}/letter-preview/generate-enhanced`,
              {
                consumer_name: consumerName,
                consumer_address: consumerAddress,
                consumer_city_state_zip: consumerCityStateZip,
                bureaus: bureaus,
                dispute_items: disputeItems.map(item => ({
                  item_id: item.item_id || item.id || '',
                  item_type: item.type || item.item_type || 'account',
                  creditor_name: item.creditor || item.name || furnisher,
                  account_number: item.accountNumber || item.account_number || '',
                  balance: item.balance || '$0.00',
                  status: item.status || '',
                  dispute_reason: item.reason || item.dispute_reason || 'Inaccurate information',
                  bureaus: normalizeBureausList(item.bureaus || bureaus),
                  bureau: normalizeBureau(item.bureau || ''),
                  method_assignment: item.methodAssignment || null,
                })),
                decision_output: decisionOutput,
                method_resolution: methodResolution ? {
                  is_mixed: methodResolution.summary?.is_mixed || false,
                  primary_method: methodResolution.summary?.primary_method,
                  unique_method_count: methodResolution.summary?.unique_method_count || 1,
                  method_groups: methodResolution.method_groups || [],
                  item_assignments: methodResolution.item_assignments || {},
                } : null,
                excluded_items: excludedItems,
                blocked_items: blockedItems,
                delivery_format: deliveryFormat,
                use_few_shot: true,
                force_deterministic: false,
              },
              { headers: getAuthHeaders(), withCredentials: true }
            );
          } catch (enhancedErr) {
            console.warn('Enhanced generation failed, falling back to standard:', enhancedErr);
            useEnhanced = false;
          }
        }
        
        // Fallback to standard endpoint
        if (!useEnhanced || !response) {
          response = await axios.post(
            `${API}/letter-preview/generate`,
            {
              letter_type: letterType,
              consumer_name: consumerName,
              consumer_address: consumerAddress,
              consumer_city_state_zip: consumerCityStateZip,
              bureaus: bureaus,
              delivery_format: deliveryFormat,
              dispute_items: disputeItems.map(item => ({
                type: item.type || 'account',
                creditor: item.creditor || item.name || furnisher,
                account_number: item.accountNumber || item.account_number || '',
                balance: item.balance || '$0.00',
                date: item.date || '',
                dispute_reason: item.reason || 'Inaccurate information',
                bureau: item.bureau || 'equifax',
                method_assignment: item.methodAssignment || null,
              })),
              furnisher: furnisher,
              violation_type: violationType,
              ai_decision: aiDecision ? {
                recommended_method: aiDecision.recommended_method,
                allowed_methods: aiDecision.allowed_methods,
                blocked_methods: aiDecision.blocked_methods,
                strategy_recommendation: aiDecision.strategy_recommendation,
                signals: aiDecision.signals,
                warnings: aiDecision.warnings,
                target_bureaus: aiDecision.target_bureaus,
                review_required: aiDecision.review_required,
              } : null,
              method_resolution: methodResolution ? {
                is_mixed: methodResolution.summary?.is_mixed || false,
                primary_method: methodResolution.summary?.primary_method,
                unique_method_count: methodResolution.summary?.unique_method_count || 1,
                method_groups: methodResolution.method_groups || [],
                item_assignments: methodResolution.item_assignments || {},
              } : null,
              generation_metadata: {
                used_fallback: usedFallback,
                fallback_reason: fallbackReason,
                url_based_type: location.state?.urlBasedType || searchParams.get('type'),
                context_notes: location.state?.contextNotes,
                is_mixed_selection: isMixedSelection,
              }
            },
            { headers: getAuthHeaders(), withCredentials: true }
          );
        }
        
        if (response.data.success) {
          // Store generation metadata for display
          const packageWithMetadata = {
            ...response.data,
            generation_metadata: {
              ai_decision: aiDecision,
              used_fallback: response.data.used_fallback ?? usedFallback,
              fallback_reason: response.data.fallback_reason ?? fallbackReason,
              effective_letter_type: letterType,
              method_used: response.data.method_used || aiDecision?.recommended_method,
              strategy_used: response.data.strategy_used || aiDecision?.strategy_recommendation?.title,
              // PHASE 4: Enhanced metadata
              generation_mode: response.data.generation_mode,
              matched_training_ids: response.data.matched_training_ids || [],
              confidence_score: response.data.confidence_score,
              explanation_summary: response.data.explanation_summary,
            },
            // PHASE 4: Include excluded/blocked items in package
            excluded_items: response.data.excluded_items || excludedItems,
            blocked_items: response.data.blocked_items || blockedItems,
            package_info: response.data.package_info || null,
          };
          setLetterPackage(packageWithMetadata);
        } else {
          throw new Error(response.data.message || 'Failed to generate preview');
        }
        
      } catch (err) {
        console.error('Preview initialization error:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };
    
    initializePreview();
  }, [location.state, searchParams, user, getAuthHeaders]);

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!letterPackage?.letters?.length) return;
    
    setDownloading(true);
    try {
      const currentLetter = letterPackage.letters[currentLetterIndex];
      const pdfFilename = buildDisputeLetterPdfFilename(currentLetter);
      
      const response = await axios.post(
        `${API}/letter-preview/generate-pdf`,
        {
          letter_html:
            currentLetter.download_pdf_html ||
            currentLetter.preview_html ||
            currentLetter.html,
          filename: pdfFilename,
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.success) {
        // Decode base64 and download
        const byteCharacters = atob(response.data.content_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.data.content_type });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || pdfFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('PDF downloaded successfully');
      }
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Handle approve and continue
  const handleApprove = async () => {
    if (!letterPackage) return;
    
    setApproving(true);
    try {
      const response = await axios.post(
        `${API}/letter-preview/approve`,
        letterPackage,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.success) {
        // CRITICAL: Link letters to the dispute round so My Disputes shows correct status
        const roundId = location.state?.roundId;
        console.log('[APPROVE] Round ID from state:', roundId);
        console.log('[APPROVE] Letter package letters:', letterPackage.letters?.length);
        
        if (roundId && letterPackage.letters?.length > 0) {
          try {
            console.log('[APPROVE] Attempting to link letters to round...');
            const linkResponse = await axios.post(
              `${API}/letter-preview/link-to-round`,
              {
                round_id: roundId,
                run_id: letterPackage.run_id || letterPackage.generation_run_id || null,
                letters: letterPackage.letters.map(letter => ({
                  bureau: letter.bureau || '',
                  html: letter.html || '',
                  method_used: letter.method_used || letter.letter_type || 'factual_inaccuracy',
                  generation_mode: letter.generation_mode || 'wizard',
                  letter_id: letter.letter_id || letter.id || null,
                })),
                user_id: user?.id || user?.user_id || '',
              },
              { headers: getAuthHeaders(), withCredentials: true }
            );
            
            if (linkResponse.data.success) {
              console.log(`[APPROVE] SUCCESS: Linked ${linkResponse.data.letters_linked} letters to round ${roundId}`);
            } else {
              console.warn('[APPROVE] Link response not successful:', linkResponse.data);
            }
          } catch (linkErr) {
            // Log but don't block - linking is an enhancement, not critical path
            console.warn('[APPROVE] Failed to link letters to round:', linkErr.response?.data || linkErr.message);
          }
        } else {
          console.warn('[APPROVE] Cannot link: roundId=', roundId, ', letters=', letterPackage.letters?.length);
        }
        
        toast.success('Letter package approved!');
        
        const lettersWithIds = (letterPackage.letters || []).map((letter, idx) => ({
          ...letter,
          id: letter.id || letter.letter_id || letter._id || `letter_${letter.bureau?.toLowerCase()}_${idx}`,
          letter_id: letter.letter_id || letter.id || letter._id || `letter_${letter.bureau?.toLowerCase()}_${idx}`,
        }));

        const resolvedPackageId =
          letterPackage?.package_id ||
          response.data.package?.package_id ||
          response.data.package?.id ||
          null;
        const step5NavState = {
          letterPackage: {
            ...(response.data.package || letterPackage),
            letters: lettersWithIds,
            package_id: resolvedPackageId,
            round_id: roundId,
          },
          approved: true,
          roundId,
          packageId: resolvedPackageId,
          package_id: resolvedPackageId,
          identityAnchor: letterPackage.consumer_info || response.data.package?.consumer_info,
          consumerInfo: letterPackage.consumer_info || response.data.package?.consumer_info,
        };

        trackWizardStep({
          metro2Service,
          roundId,
          token,
          currentStep: 'supporting_documents',
          lastCompletedStep: 'preview',
          packageId: resolvedPackageId,
          wizardStatus: 'pending_attachments',
          generatedLetterIds: lettersWithIds.map((l) => l.letter_id || l.id).filter(Boolean),
          navigationState: {
            letterPackage: step5NavState.letterPackage,
            identityAnchor: step5NavState.identityAnchor,
          },
        });

        saveFinalPackageSnapshot({
          letterPackage: step5NavState.letterPackage,
          stepState: step5NavState,
          roundId,
          packageId: resolvedPackageId,
          identityAnchor: step5NavState.identityAnchor,
          family: location.state?.letterType || letterPackage?.strategy,
        });

        navigate(buildFinalPackageUrl({ packageId: resolvedPackageId, roundId }), {
          state: step5NavState,
        });
      }
    } catch (err) {
      console.error('Approve error:', err);
      toast.error('Failed to approve package');
    } finally {
      setApproving(false);
    }
  };

  // Handle save draft
  const handleSaveDraft = async () => {
    if (!letterPackage) return;
    
    try {
      const response = await axios.post(
        `${API}/letter-preview/save-draft`,
        {
          ...letterPackage,
          consumer_id: user?.id || '',
          case_id: location.state?.caseId || ''
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success('Draft saved successfully');
      }
    } catch (err) {
      console.error('Save draft error:', err);
      toast.error('Failed to save draft');
    }
  };

  // Handle back to edit
  const handleBackToEdit = () => {
    // Navigate back to item selection with preserved state
    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 mb-2">Generating Letter Preview</h3>
          <p className="text-stone-600">Creating real letter content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 mb-2">Preview Error</h3>
          <p className="text-stone-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/dispute-wizard', { replace: true })}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Generate
          </Button>
        </div>
      </div>
    );
  }

  if (!loading && (!letterPackage?.letters || letterPackage.letters.length === 0)) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 mb-2">No Letters Available</h3>
          <p className="text-stone-600 mb-4">
            Letters were not generated yet. Please retry generation.
          </p>
          <Button onClick={() => navigate('/dispute-wizard', { replace: true })}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Generate
          </Button>
        </div>
      </div>
    );
  }

  const currentLetter = letterPackage?.letters?.[currentLetterIndex];
  
  // Extract protected anchors from location state or letter package
  const protectedAnchors = location.state?.protectedAnchors || 
                          letterPackage?.protected_anchors || 
                          {
                            legal_name: location.state?.consumerName,
                            current_address: location.state?.consumerAddress,
                            dob: location.state?.consumerDOB,
                          };
  
  // Validation status based on letter metadata
  const validationStatus = {
    bureau_specific: true,
    protected_anchors: true,
    pdf_ready: !!currentLetter?.html,
    no_leakage: true,
  };
  
  // Handle download for a single letter
  const handleDownloadSingleLetter = async (letter) => {
    if (!letter?.html) return;
    
    setDownloading(true);
    try {
      const pdfFilename = buildDisputeLetterPdfFilename(letter);
      // Build request with signature settings if present
      const requestBody = {
        letter_html:
          letter.download_pdf_html || letter.preview_html || letter.html,
        filename: pdfFilename,
      };
      
      // Include signature settings if provided
      if (letter.signatureSettings) {
        requestBody.signature_settings = {
          enabled: letter.signatureSettings.enabled,
          name: letter.signatureSettings.name,
          style: letter.signatureSettings.style,
          applied: letter.signatureSettings.applied || true
        };
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/letter-preview/download-pdf`,
        requestBody,
        {
          headers: getAuthHeaders(),
          responseType: 'blob',
        }
      );
      
      // Create download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = requestBody.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${letter.bureau} letter downloaded successfully`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };
  
  // Handle download all letters
  const handleDownloadAllLetters = async () => {
    if (!letterPackage?.letters?.length) return;
    
    for (const letter of letterPackage.letters) {
      await handleDownloadSingleLetter(letter);
    }
  };
  
  // Handle print
  const handlePrint = () => {
    const iframe = document.querySelector('[data-testid="letter-preview-iframe"]');
    if (iframe?.contentWindow) {
      iframe.contentWindow.print();
    }
  };
  
  // Handle copy
  const handleCopy = async () => {
    if (!currentLetter?.html) return;
    
    try {
      const temp = document.createElement('div');
      temp.innerHTML = currentLetter.preview_html || currentLetter.html;
      const text = temp.textContent || temp.innerText;
      await navigator.clipboard.writeText(text);
      toast.success('Letter text copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy letter');
    }
  };

  // Get consumer name for digital signature
  const consumerName = user?.full_name || user?.name || 
                       letterPackage?.consumer_info?.name ||
                       letterPackage?.letters?.[0]?.consumer_name ||
                       'Consumer Name';

  return (
    <EnhancedLetterPreview
      letters={letterPackage?.letters || []}
      currentIndex={currentLetterIndex}
      onSelectLetter={setCurrentLetterIndex}
      onDownload={handleDownloadSingleLetter}
      onDownloadAll={handleDownloadAllLetters}
      onPrint={handlePrint}
      onBack={handleBackToEdit}
      onApprove={handleApprove}
      downloading={downloading}
      protectedAnchors={protectedAnchors}
      validationStatus={validationStatus}
      consumerName={consumerName}
    />
  );
};

export default LetterPreviewPage;
